/**
 * Multiplayer Client — singleton WebSocket client connecting to
 * SwitcherSIM's /ws/game endpoint as a GRAPHICS_OPERATOR observer.
 *
 * When SwitcherSIM broadcasts `program_changed`, triggers the
 * registered handler to swap the live camera video URL.
 *
 * Protocol matches switchersim-v2's WebSocket server:
 *   1. Client connects to ws://host/ws/game
 *   2. Client sends { type: 'JOIN_ROOM', roomCode, playerToken }
 *   3. Server responds with ROOM_STATE / PLAYER_JOINED / PROGRAM_CHANGED etc.
 *   4. Ping/pong keepalive at ROOM.PING_INTERVAL_MS
 */

import type { GfxServerMessage, SwitcherRoomSnapshot } from '@graphicsim/shared-types';
import { ROOM } from '@graphicsim/shared-constants';
import { multiplayerStore } from '../stores/multiplayerStore';

type ProgramChangedHandler = (input: number, videoUrl?: string) => void;
type RoomStateHandler = (snapshot: SwitcherRoomSnapshot) => void;
type DisconnectHandler = () => void;

let ws: WebSocket | null = null;
let pingInterval: ReturnType<typeof setInterval> | null = null;
let reconnectAttempts = 0;
let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
let shouldReconnect = false;

// Saved connection params for reconnect
let savedWsUrl = '';
let savedRoomCode = '';
let savedPlayerToken = '';

// Event handlers
let onProgramChangedHandler: ProgramChangedHandler | null = null;
let onRoomStateHandler: RoomStateHandler | null = null;
let onDisconnectHandler: DisconnectHandler | null = null;

/** Register handler for program input changes */
export function onProgramChanged(handler: ProgramChangedHandler): void {
  onProgramChangedHandler = handler;
}

/** Register handler for initial room state */
export function onRoomState(handler: RoomStateHandler): void {
  onRoomStateHandler = handler;
}

/** Register handler for disconnection */
export function onDisconnect(handler: DisconnectHandler): void {
  onDisconnectHandler = handler;
}

/** Connect to SwitcherSIM's WebSocket as graphics observer */
export function connect(wsUrl: string, roomCode: string, playerToken: string): void {
  disconnect(); // clean up any existing connection

  savedWsUrl = wsUrl;
  savedRoomCode = roomCode;
  savedPlayerToken = playerToken;
  shouldReconnect = true;
  reconnectAttempts = 0;

  createConnection();
}

/** Disconnect and stop reconnecting */
export function disconnect(): void {
  shouldReconnect = false;

  if (reconnectTimeout !== null) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }

  if (pingInterval !== null) {
    clearInterval(pingInterval);
    pingInterval = null;
  }

  if (ws) {
    ws.onclose = null; // prevent reconnect
    ws.close();
    ws = null;
  }

  multiplayerStore.getState().setConnected(false);
}

/** Check if currently connected */
export function isConnected(): boolean {
  return ws !== null && ws.readyState === WebSocket.OPEN;
}

// --- Internal ---

function createConnection(): void {
  try {
    ws = new WebSocket(savedWsUrl);
  } catch (err) {
    console.error('[MultiplayerClient] WebSocket creation failed:', err);
    scheduleReconnect();
    return;
  }

  ws.onopen = () => {
    console.info(`[MultiplayerClient] Connected to ${savedWsUrl}`);
    reconnectAttempts = 0;

    // Send JOIN_ROOM message (matches switchersim-v2 protocol)
    send({
      type: 'JOIN_ROOM',
      roomCode: savedRoomCode,
      playerToken: savedPlayerToken,
    });

    // Start ping keepalive
    pingInterval = setInterval(() => {
      if (ws?.readyState === WebSocket.OPEN) {
        send({ type: 'PING' });
      }
    }, ROOM.PING_INTERVAL_MS);

    multiplayerStore.getState().setConnected(true);
  };

  ws.onmessage = (event) => {
    try {
      const msg = JSON.parse(event.data as string) as GfxServerMessage;
      handleMessage(msg);
    } catch (err) {
      console.warn('[MultiplayerClient] Failed to parse message:', err);
    }
  };

  ws.onclose = () => {
    console.info('[MultiplayerClient] Connection closed');
    cleanup();
    multiplayerStore.getState().setConnected(false);
    onDisconnectHandler?.();
    scheduleReconnect();
  };

  ws.onerror = (err) => {
    console.warn('[MultiplayerClient] WebSocket error:', err);
  };
}

function handleMessage(msg: GfxServerMessage): void {
  switch (msg.type) {
    case 'room_state': {
      const snapshot = msg.payload;
      multiplayerStore.getState().setVideoUrls(snapshot.videoUrls);
      multiplayerStore.getState().setLiveCameraInput(
        snapshot.switcher.program,
        snapshot.videoUrls[snapshot.switcher.program],
      );
      onRoomStateHandler?.(snapshot);
      break;
    }

    case 'program_changed': {
      const { input, videoUrl } = msg.payload;
      multiplayerStore.getState().setLiveCameraInput(input, videoUrl);
      onProgramChangedHandler?.(input, videoUrl);
      break;
    }

    case 'simulation_started':
      console.info(`[MultiplayerClient] Simulation started: ${msg.payload.lessonId}`);
      break;

    case 'simulation_ended':
      console.info(`[MultiplayerClient] Simulation ended: ${msg.payload.lessonId}`);
      break;

    case 'pong':
      // Keepalive acknowledged
      break;

    case 'error':
      console.error(`[MultiplayerClient] Server error: ${msg.payload.message}`);
      break;

    default:
      // Ignore other message types
      break;
  }
}

function send(data: Record<string, unknown>): void {
  if (ws?.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(data));
  }
}

function cleanup(): void {
  if (pingInterval !== null) {
    clearInterval(pingInterval);
    pingInterval = null;
  }
}

function scheduleReconnect(): void {
  if (!shouldReconnect) return;
  if (reconnectAttempts >= ROOM.MAX_RECONNECT_ATTEMPTS) {
    console.warn('[MultiplayerClient] Max reconnect attempts reached');
    multiplayerStore.getState().reset();
    return;
  }

  const delay = ROOM.RECONNECT_DELAY_MS * Math.pow(2, reconnectAttempts);
  reconnectAttempts++;
  console.info(`[MultiplayerClient] Reconnecting in ${delay}ms (attempt ${reconnectAttempts})`);

  reconnectTimeout = setTimeout(() => {
    reconnectTimeout = null;
    createConnection();
  }, delay);
}
